import { KbSlider } from '@theme/kb-slider';
import { prefersReducedMotion } from '@theme/utilities';

if (!customElements.get('hero-carousel')) {
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.slides = [];
      this.slideCount = 0;
      this.activeIndex = 0;

      this.autoplayEnabled = false;
      this.autoplayInterval = 5000;
      this.autoplayTimer = null;
      this.slideVideoAutoplayEnabled = false;
      this.userPausedIndex = null;
      this.videoStartTimer = null;
      this.scheduledVideoIndex = -1;

      this.isSetup = false;
      this.isPointerOver = false;
      this.abortController = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleInteractionStart = this.handleInteractionStart.bind(this);
      this.handleInteractionEnd = this.handleInteractionEnd.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
    }

    connectedCallback() {
      if (this.isSetup) {
        return;
      }

      this.track =
        this.querySelector('[data-hero-carousel-track]') ||
        this.querySelector('.hero-carousel__track');

      this.slides = Array.from(
        this.querySelectorAll('[data-hero-carousel-slide]')
      );

      this.progress = this.querySelector(
        '[data-hero-carousel-progress]'
      );

      this.slideCount = this.slides.length;

      if (!this.track || this.slideCount === 0) {
        return;
      }

      this.isSetup = true;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.slideVideoAutoplayEnabled =
        this.dataset.slideVideoAutoplay === 'true';
      this.autoplayInterval = Number.parseInt(this.dataset.autoplayInterval, 10) || 5000;
      this.abortController = new AbortController();

      this.style.setProperty(
        '--hero-carousel-slide-count',
        this.slideCount
      );

      this.disableNativeMediaDragging();
      this.addEventListeners();

      if (this.slideCount === 1) {
        this.classList.add('is-initialised');
        this.updateActiveState(0);
        return;
      }

      this.initialiseSlider();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.pauseAllVideos();

      this.abortController?.abort();
      this.abortController = null;

      this.slider?.destroy();
      this.slider = null;

      this.classList.remove('is-initialised');

      if (this.videoStartTimer) {
        window.clearTimeout(this.videoStartTimer);
        this.videoStartTimer = null;
      }

      this.isSetup = false;
      this.isPointerOver = false;
      this.activeIndex = 0;
      this.userPausedIndex = null;
      this.scheduledVideoIndex = -1;
    }

    initialiseSlider() {
      try {
        this.slider = new KbSlider(this.track, {
          loop: this.slideCount >= 3,
          cloneBlocksPerSide: this.isCompactViewport() ? 1 : 2,
          programmaticScrollMs: this.getTransitionDuration() + 200,
          drag: true,
          keyboard: true,
          snapAlign: 'auto',
          ariaLabel: 'Hero carousel',
          progressHost: this,
          transitionMs: this.getTransitionDuration(),
          onChange: ({ index, trigger }) => {
            this.handleSliderChange(index, trigger);
          },
          onSettle: () => {
            this.handleSliderSettle();
          }
        });

        this.slider.setSlides(this.slides);

        this.classList.add('is-initialised');
        this.updateActiveState(this.slider.index);
        this.startAutoplay();
      } catch (error) {
        console.error(
          'Hero Carousel: Failed to initialise.',
          error
        );

        this.slider = null;

        this.classList.add('is-initialised');
        this.updateActiveState(0);
      }
    }

    isCompactViewport() {
      return !window.matchMedia('(min-width: 990px)').matches;
    }

    addEventListeners() {
      const signal = this.abortController.signal;

      this.addEventListener('click', this.handleClick, {
        signal
      });

      this.addEventListener(
        'pointerenter',
        this.handlePointerEnter,
        { signal }
      );

      this.addEventListener(
        'pointerleave',
        this.handlePointerLeave,
        { signal }
      );

      this.addEventListener('ended', this.handleVideoEnded, {
        capture: true,
        signal
      });

      this.track.addEventListener(
        'pointerdown',
        this.handleInteractionStart,
        { signal }
      );

      this.track.addEventListener(
        'pointerup',
        this.handleInteractionEnd,
        { signal }
      );

      this.track.addEventListener(
        'pointercancel',
        this.handleInteractionEnd,
        { signal }
      );

      this.track.addEventListener(
        'touchstart',
        this.handleInteractionStart,
        {
          passive: true,
          signal
        }
      );

      this.track.addEventListener(
        'touchend',
        this.handleInteractionEnd,
        {
          passive: true,
          signal
        }
      );

      this.track.addEventListener(
        'touchcancel',
        this.handleInteractionEnd,
        {
          passive: true,
          signal
        }
      );

      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
        { signal }
      );

      if (window.Shopify?.designMode) {
        document.addEventListener(
          'shopify:block:select',
          this.handleBlockSelect,
          { signal }
        );
      }
    }

    handleSliderChange(index, trigger) {
      if (index !== this.activeIndex) {
        this.pauseAllVideos();
      }

      this.updateActiveState(index);

      /*
       * The scroll trigger also fires while an animated move
       * is still running and while the loop re-anchors onto
       * a real slide, reporting clones along the way. It
       * must never restart the timer or the interval drifts
       * by a second or more. A drag restarts the timer from
       * handleInteractionEnd instead.
       */
      if (trigger === 'scroll') {
        return;
      }

      if (trigger === 'autoplay') {
        this.startAutoplay();
        return;
      }

      this.restartAutoplay();
    }

    handleSliderSettle() {
      this.updateActiveState(this.slider?.index ?? 0);
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const mediaControl = event.target.closest(
        '[data-hero-carousel-media-control]'
      );

      if (!mediaControl || !this.contains(mediaControl)) {
        return;
      }

      const slide = mediaControl.closest(
        '[data-hero-carousel-slide]'
      );

      if (!slide) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.toggleVideo(slide, mediaControl);
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const selectedSlide = event.target.closest(
        '[data-hero-carousel-slide]'
      );

      if (
        !selectedSlide ||
        !this.contains(selectedSlide) ||
        !this.slider
      ) {
        return;
      }

      const selectedIndex = this.slides.indexOf(selectedSlide);

      if (selectedIndex < 0) {
        return;
      }

      this.stopAutoplay();
      this.pauseAllVideos();

      this.slider.goTo(selectedIndex, {
        animate: false,
        trigger: 'editor'
      });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        this.pauseAllVideos();
        return;
      }

      this.startAutoplay();
    }

    handlePointerEnter() {
      this.isPointerOver = true;
      this.stopAutoplay();
    }

    handlePointerLeave() {
      this.isPointerOver = false;
      this.startAutoplay();
    }

    handleInteractionStart() {
      this.stopAutoplay();
    }

    handleInteractionEnd() {
      window.requestAnimationFrame(() => {
        this.startAutoplay();
      });
    }

    handleVideoEnded(event) {
      const video = event.target;

      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      const slide = video.closest(
        '[data-hero-carousel-slide]'
      );

      const control = slide?.querySelector(
        '[data-hero-carousel-media-control]'
      );

      if (!slide || !control) {
        return;
      }

      this.setVideoState(slide, control, false);
      this.startAutoplay();
    }

    updateActiveState(index) {
      const boundedIndex = Math.min(
        Math.max(Number.isInteger(index) ? index : 0, 0),
        this.slideCount - 1
      );

      if (boundedIndex !== this.activeIndex) {
        this.userPausedIndex = null;
      }

      this.activeIndex = boundedIndex;

      this.progress?.setAttribute(
        'aria-valuenow',
        boundedIndex + 1
      );

      this.updateRenderedSlides();
      this.updateVideoControls();

      if (this.scheduledVideoIndex !== boundedIndex) {
        this.scheduledVideoIndex = boundedIndex;
        this.scheduleActiveSlideVideo();
      }
    }

    scheduleActiveSlideVideo() {
      if (this.videoStartTimer) {
        window.clearTimeout(this.videoStartTimer);
        this.videoStartTimer = null;
      }

      if (!this.slideVideoAutoplayEnabled) {
        return;
      }

      /*
       * Warm the slide in view and the one after it. A
       * transition alone is not long enough to buffer a
       * 1080p clip, so the next one gets a whole interval
       * of head start.
       */
      this.warmVideo(this.activeIndex);
      this.warmVideo(this.activeIndex + 1);

      /*
       * Starting the video on the change would play it
       * while the previous slide is still the one in view.
       * The move eases out though, so by this fraction of
       * the transition the slide has covered about ninety
       * per cent of its travel and reads as arrived.
       */
      const arrivalFraction = 0.55;

      this.videoStartTimer = window.setTimeout(() => {
        this.videoStartTimer = null;
        this.playActiveSlideVideo();
      }, Math.round(this.getTransitionDuration() * arrivalFraction));
    }

    updateRenderedSlides() {
      const renderedSlides = Array.from(
        this.track.querySelectorAll(
          '[data-hero-carousel-slide]'
        )
      );

      renderedSlides.forEach((slide) => {
        const realIndex = this.getRenderedSlideIndex(slide);
        const isActive = realIndex === this.activeIndex;

        slide.classList.toggle('is-active', isActive);

        slide.classList.toggle(
          'hero-carousel__slide--active',
          isActive
        );

        if (slide.dataset.kbSliderClone) {
          return;
        }

        slide.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });
    }

    updateVideoControls() {
      const renderedSlides = Array.from(
        this.track.querySelectorAll(
          '[data-hero-carousel-slide]'
        )
      );

      renderedSlides.forEach((slide) => {
        const control = slide.querySelector(
          '[data-hero-carousel-media-control]'
        );

        if (!control) {
          return;
        }

        const isClone = Boolean(
          slide.dataset.kbSliderClone
        );

        const isActive =
          slide.classList.contains('is-active');

        control.tabIndex =
          !isClone && isActive ? 0 : -1;

        const video = slide.querySelector(
          '[data-hero-carousel-video]'
        );

        if (!video) {
          return;
        }

        if (isClone) {
          /*
           * A clone never plays, and its poster covers the
           * video anyway. On a phone the element is dropped
           * outright, because a handful of idle video
           * elements is what makes the carousel stutter
           * there. Desktop keeps them so a clone beside the
           * centre still answers its play button.
           */
          if (this.isCompactViewport()) {
            video.remove();
            return;
          }

          video.preload = 'none';
          return;
        }

        if (isActive && video.preload === 'none') {
          video.preload = 'metadata';
        }
      });
    }

    warmVideo(index) {
      const slide = this.slides[index % this.slideCount];
      const video = slide?.querySelector(
        '[data-hero-carousel-video]'
      );

      if (
        !video ||
        video.preload === 'auto' ||
        !video.paused
      ) {
        return;
      }

      video.preload = 'auto';
      video.load();
    }

    playActiveSlideVideo() {
      if (
        !this.slideVideoAutoplayEnabled ||
        prefersReducedMotion() ||
        this.userPausedIndex === this.activeIndex
      ) {
        return;
      }

      const slide = this.slides[this.activeIndex];

      if (!slide) {
        return;
      }

      const video = slide.querySelector(
        '[data-hero-carousel-video]'
      );

      const control = slide.querySelector(
        '[data-hero-carousel-media-control]'
      );

      if (!video || !control || !video.paused) {
        return;
      }

      this.playVideo(video, slide, control, {
        keepAutoplay: true
      });
    }

    toggleVideo(slide, control) {
      const video = slide.querySelector(
        '[data-hero-carousel-video]'
      );

      if (!video) {
        return;
      }

      if (video.paused || video.ended) {
        this.userPausedIndex = null;
        this.playVideo(video, slide, control);
        return;
      }

      /*
       * Remember the pause so slide video autoplay does
       * not start the same slide again behind the visitor.
       */
      this.userPausedIndex = this.activeIndex;

      this.pauseVideo(video, slide, control);
    }

    async playVideo(video, slide, control, options = {}) {
      const { keepAutoplay = false } = options;

      /*
       * A video the visitor chose holds the carousel. One
       * the section started is ambient and must not touch
       * the timer, or the interval becomes the interval
       * plus the transition plus however long the clip
       * takes to buffer.
       */
      if (!keepAutoplay) {
        this.stopAutoplay();
      }

      this.pauseAllVideos(video);

      /*
       * A video at preload none can take a moment to start.
       * The control flips straight away so the click is
       * answered, while the poster only lifts once playback
       * really begins, which avoids a flash of black.
       */
      this.setControlState(control, true);

      try {
        await video.play();
        this.setVideoState(slide, control, true);
      } catch (error) {
        this.setVideoState(slide, control, false);

        if (!keepAutoplay) {
          this.startAutoplay();
        }
      }
    }

    pauseVideo(video, slide, control) {
      video.pause();
      this.setVideoState(slide, control, false);
      this.startAutoplay();
    }

    setVideoState(slide, control, isPlaying) {
      slide.classList.toggle('is-playing', isPlaying);
      this.setControlState(control, isPlaying);
    }

    setControlState(control, isPlaying) {
      control.setAttribute(
        'aria-pressed',
        String(isPlaying)
      );

      control.setAttribute(
        'aria-label',
        isPlaying ? 'Pause video' : 'Play video'
      );
    }

    pauseAllVideos(excludedVideo = null) {
      const videos = Array.from(
        this.querySelectorAll(
          '[data-hero-carousel-video]'
        )
      );

      videos.forEach((video) => {
        if (video === excludedVideo) {
          return;
        }

        if (!video.paused) {
          video.pause();
        }

        const slide = video.closest(
          '[data-hero-carousel-slide]'
        );

        const control = slide?.querySelector(
          '[data-hero-carousel-media-control]'
        );

        if (!slide || !control) {
          return;
        }

        this.setVideoState(slide, control, false);
      });
    }

    isAutoplayBlockedByVideo() {
      /*
       * A video the section started itself is ambient and
       * must not hold the carousel. One the visitor started
       * is being watched, so it must.
       */
      if (this.slideVideoAutoplayEnabled) {
        return false;
      }

      return this.hasPlayingVideo();
    }

    hasPlayingVideo() {
      return Array.from(
        this.querySelectorAll(
          '[data-hero-carousel-video]'
        )
      ).some((video) => !video.paused && !video.ended);
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        !this.autoplayEnabled ||
        !this.slider ||
        this.slideCount <= 1 ||
        prefersReducedMotion() ||
        document.hidden ||
        this.isPointerOver ||
        this.isAutoplayBlockedByVideo()
      ) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;

        if (!this.slider || this.isAutoplayBlockedByVideo()) {
          return;
        }

        this.advance();
      }, this.autoplayInterval);
    }

    advance() {
      /*
       * Without clones next() stands still on the last
       * slide, so rewind to the first one rather than let
       * autoplay die there.
       */
      const isOnLastSlide =
        this.slider.index >= this.slider.count - 1;

      if (isOnLastSlide && !this.slider.looping) {
        this.slider.goTo(0, { trigger: 'autoplay' });
        return;
      }

      this.slider.next('autoplay');
    }

    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    getRenderedSlideIndex(slide) {
      const cloneIndex = Number.parseInt(
        slide.dataset.kbSliderRealIndex,
        10
      );

      if (Number.isInteger(cloneIndex)) {
        return cloneIndex;
      }

      return this.slides.indexOf(slide);
    }

    disableNativeMediaDragging() {
      this.querySelectorAll('img, video').forEach((media) => {
        media.draggable = false;
      });
    }

    getTransitionDuration() {
      const propertyValue = window
        .getComputedStyle(this)
        .getPropertyValue(
          '--hero-carousel-transition-duration'
        )
        .trim();

      if (propertyValue.endsWith('ms')) {
        return Number.parseFloat(propertyValue);
      }

      if (propertyValue.endsWith('s')) {
        return Number.parseFloat(propertyValue) * 1000;
      }

      return 1000;
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
}